import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";
import api from "./api";
import socketManager from "./socket";
import toast from "react-hot-toast";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          if (decoded.exp * 1000 > Date.now()) {
            setUser(decoded);
            Sentry.setUser({
              id: decoded.id,
              email: decoded.email,
              role: decoded.role,
            });

            // ✅ Only auto-navigate if NOT on an auth/public page
            const hash = window.location.hash;
            const isPublicPage = ["/signin", "/signup", "/verify"].some((p) =>
              hash.includes(p),
            );

            if (!isPublicPage) {
              if (decoded.role === "admin") navigate("/admin");
              else if (decoded.role === "clinician") navigate("/dashboard");
              else navigate("/patient");
            }
          } else {
            localStorage.removeItem("token");
          }
        } catch (e) {
          localStorage.removeItem("token");
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (user?.id && token) {
      socketManager.connect(user.id, token);
    }
  }, [user?.id]);

  const signIn = async (email, password) => {
    try {
      const { data } = await api.post("/users/signin", { email, password });

      if (data.mfa_required) {
        return {
          success: true,
          mfa_required: true,
          mfa_token: data.mfa_pending_token,
          user_id: data.user_id,
        };
      }

      return handleAuthSuccess(data);
    } catch (err) {
      console.error("Sign in failed:", err.response?.data || err);
      return {
        success: false,
        error: err.response?.data?.message || "Authentication Failed",
      };
    }
  };

  const verifyMfa = async (mfaToken, code, userId) => {
    try {
      const { data } = await api.post("/users/mfa/totp/challenge", {
        token: mfaToken,
        code,
        userId,
      });
      return handleAuthSuccess(data);
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "Invalid MFA Code",
      };
    }
  };

  /**
   * Hardware Key (FIDO2) Handshake - v2.29.0
   * Implements navigator.credentials.get() for sensitive step-up actions.
   */
  const verifyHardwareKey = async () => {
    try {
      // 1. Get assertion options from server
      const { data: options } = await api.post("/auth/mfa/webauthn/challenge");

      // 2. Decode challenge from base64
      const challenge = Uint8Array.from(atob(options.challenge), (c) =>
        c.charCodeAt(0),
      );

      // 3. Request Assertion from Hardware Key
      const assertion = await navigator.credentials.get({
        publicKey: {
          ...options,
          challenge: challenge,
          userVerification: "required",
        },
      });

      if (!assertion) throw new Error("No hardware response");

      // 4. Send assertion back for server-side verification
      const { data: result } = await api.post("/auth/mfa/webauthn/verify", {
        assertion: {
          id: assertion.id,
          rawId: btoa(String.fromCharCode(...new Uint8Array(assertion.rawId))),
          type: assertion.type,
          response: {
            authenticatorData: btoa(
              String.fromCharCode(
                ...new Uint8Array(assertion.response.authenticatorData),
              ),
            ),
            clientDataJSON: btoa(
              String.fromCharCode(
                ...new Uint8Array(assertion.response.clientDataJSON),
              ),
            ),
            signature: btoa(
              String.fromCharCode(
                ...new Uint8Array(assertion.response.signature),
              ),
            ),
            userHandle: assertion.response.userHandle
              ? btoa(
                  String.fromCharCode(
                    ...new Uint8Array(assertion.response.userHandle),
                  ),
                )
              : null,
          },
        },
        challenge: options.challenge,
      });

      return result.verified;
    } catch (err) {
      console.error("WebAuthn Step-up Error:", err);
      toast.error(
        err.name === "NotAllowedError"
          ? "Verification cancelled by user"
          : "Hardware verification failed",
      );
      return false;
    }
  };

  const handleAuthSuccess = (data) => {
    localStorage.setItem("token", data.token);
    setUser(data.user);
    Sentry.setUser({
      id: data.user.id,
      email: data.user.email,
      role: data.user.role,
    });

    return {
      success: true,
      token: data.token,
      user: data.user,
    };
  };

  const signUp = async (userData) => {
    try {
      const { data } = await api.post("/users/signup", userData);
      
      return { success: true, data };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message,
      };
    }
  };

  const signOut = () => {
    localStorage.removeItem("token");
    socketManager.disconnect();
    setUser(null);
    Sentry.setUser(null);
    navigate("/signin");
    toast.success("Signed out securely");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        verifyMfa,
        verifyHardwareKey,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
